import * as React from "react";

import { cn } from "@/lib/utils";

// ALICE / Status Pill (Figma: Light 2015:38, Dark 2014:38). One component for every status chip so
// pills stay uniform across pages. Values are read from the Figma component; the set is identical in
// Light and Dark. "On colour" sits on the gradient, on panes and anywhere in Dark; "On white" sits in
// a Light pop-up or a #E5EFFA panel.
const TONES = {
  healthy: {
    colour: { tint: "rgba(63,211,126,0.18)", dot: "#3FD37E", ink: "#8AF0B4" },
    white: { tint: "rgba(22,163,74,0.12)", dot: "#16A34A", ink: "#15803D" },
  },
  attention: {
    colour: { tint: "rgba(245,176,39,0.18)", dot: "#F5B027", ink: "#F7CE7C" },
    white: { tint: "rgba(217,119,6,0.12)", dot: "#D97706", ink: "#B45309" },
  },
  risk: {
    colour: { tint: "rgba(255,107,91,0.18)", dot: "#FF6B5B", ink: "#FFB1A6" },
    white: { tint: "rgba(220,38,38,0.12)", dot: "#DC2626", ink: "#B91C1C" },
  },
  onboarding: {
    colour: { tint: "rgba(77,168,240,0.18)", dot: "#4DA8F0", ink: "#A9D4FA" },
    white: { tint: "rgba(37,99,235,0.12)", dot: "#2563EB", ink: "#1D4ED8" },
  },
  live: {
    colour: { tint: "rgba(155,107,255,0.18)", dot: "#9B6BFF", ink: "#C9B8FF" },
    white: { tint: "rgba(124,58,237,0.12)", dot: "#7C3AED", ink: "#6D28D9" },
  },
};

// The seven design statuses, plus the status words pages already use. Keep page-specific
// mappings here, in one place, rather than in each page.
const STATUS_TONE = {
  healthy: "healthy", connected: "healthy", completed: "healthy", success: "healthy",
  succeeded: "healthy", compliant: "healthy", "up to date": "healthy", approved: "healthy", passed: "healthy",
  attention: "attention", pending: "attention", warning: "attention", "in progress": "attention",
  in_progress: "attention", testing: "attention", processing: "attention", paused: "attention",
  "at risk": "risk", at_risk: "risk", failed: "risk", error: "risk", rejected: "risk", "non-compliant": "risk",
  onboarding: "onboarding", info: "onboarding", scheduled: "onboarding", queued: "onboarding", new: "onboarding",
  live: "live",
};

export function statusTone(status) {
  return STATUS_TONE[String(status || "").trim().toLowerCase()] || "onboarding";
}

export const StatusPill = React.forwardRef(({ status, tone, surface = "colour", className, children, ...props }, ref) => {
  const t = TONES[tone || statusTone(status)][surface === "white" ? "white" : "colour"];
  return (
    <span
      ref={ref}
      className={cn("inline-flex items-center gap-[6px] rounded-full px-[11px] py-[4px] text-[12px] font-semibold leading-normal whitespace-nowrap", className)}
      // On colour, the tint sits on --pill-base (a dark wash in Light, none in Dark; theme.css) so the
      // pastel label stays above 3:1 on the bright Light gradient.
      style={{ background: surface === "white" ? t.tint : `linear-gradient(${t.tint}, ${t.tint}), var(--pill-base, transparent)`, color: t.ink }}
      {...props}
    >
      <span aria-hidden="true" className="h-[6px] w-[6px] shrink-0 rounded-full" style={{ background: t.dot }} />
      {children ?? status}
    </span>
  );
});
StatusPill.displayName = "StatusPill";
